<template>
    <div class="table-pagination">
        <div class="table-pagination__limit">
            <span class="typographies-paragraph-small-semibold">
                {{ $t.__('ListingWrapper.pagination_text') }}
            </span>
            <CompassSelect
                :modelValue="pagination.limit"
                menuClass="table-pagination__select-menu"
                class="table-pagination__select"
                size="small"
                @update:modelValue="handleLimitChange"
            >
                <CompassDropdownOption
                    v-for="(option, index) in paginationLimitOptions"
                    :key="index"
                    :value="option"
                    :label="option.toString()"
                />
            </CompassSelect>
        </div>
        <div class="table-pagination__navigation">
            <CompassIconButton
                size="extra-small"
                icon="caret-left"
                :disabled="isPreviousPageDisabled"
                @click="previousPage"
            />
            <span class="typographies-paragraph-small-semibold">
                {{ textPagination }}
            </span>
            <CompassIconButton
                size="extra-small"
                icon="caret-right"
                :disabled="isNextPageDisabled"
                @click="nextPage"
            />
        </div>
    </div>
</template>

<script>
export default {
    props: {
        isLoading: {
            type: Boolean,
            required: true,
        },
        pagination: {
            type: Object,
            required: true,
        },
        paginationLimitOptions: {
            type: Array,
            default: () => [25, 50, 75, 100],
        },
    },
    emits: ['update-pagination'],
    data() {
        return {
            pageCount: this.computePageFromOffset(),
            listPreviousOffset: [null],
        };
    },
    computed: {
        count() {
            if (this.pageCount === 1) {
                return 1;
            }

            return (this.pageCount - 1) * this.pagination.limit + 1;
        },
        endCount() {
            let endCount =
                this.count < this.pagination.total
                    ? this.pageCount * this.pagination.limit
                    : this.pagination.total;
            if (this.pagination.total < this.pagination.limit || endCount > this.pagination.total) {
                endCount = this.pagination.total;
            }
            return endCount;
        },
        textPagination() {
            return this.$t.__('common.pagination.count_page', {
                currentPage: this.count,
                currentItemsCount: this.endCount,
                totalItems: this.pagination.total,
            });
        },
        isPreviousPageDisabled() {
            if (this.isLoading) {
                return true;
            }
            return this.pageCount === 1;
        },
        isNextPageDisabled() {
            if (this.isLoading) {
                return true;
            }
            return (
                this.pagination.limit > this.pagination.total ||
                this.endCount === this.pagination.total
            );
        },
    },
    watch: {
        // Resync local pageCount when the parent resets pagination
        // (e.g., navigating between listings). Only treat a falsy offset as a reset:
        // cursor-based pagination returns opaque tokens that change on every page.
        'pagination.offset'(newOffset) {
            if (!newOffset && this.pageCount !== 1) {
                this.pageCount = 1;
                this.listPreviousOffset = [null];
            }
        },
    },
    methods: {
        computePageFromOffset() {
            const offset = Number(this.pagination.offset);
            if (!Number.isFinite(offset) || !this.pagination.limit) {
                return 1;
            }
            return Math.floor(offset / this.pagination.limit) + 1;
        },
        handleLimitChange(value) {
            this.listPreviousOffset = [null];
            this.$emit('update-pagination', {
                ...this.pagination,
                limit: value,
                offset: null,
                currentPage: 1,
            });
            this.pageCount = 1;
        },
        previousPage() {
            this.pageCount -= 1;
            this.listPreviousOffset.pop();

            this.$emit('update-pagination', {
                ...this.pagination,
                offset: this.listPreviousOffset[this.listPreviousOffset.length - 1],
                currentPage: this.pageCount,
            });
        },
        nextPage() {
            if (!this.listPreviousOffset.includes(this.pagination.offset)) {
                this.listPreviousOffset.push(this.pagination.offset);
                this.pageCount += 1;
            }
            this.$emit('update-pagination', { ...this.pagination, currentPage: this.pageCount });
        },
    },
};
</script>

<style lang="scss" scoped>
.table-pagination {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-6);
    color: var(--colors-common-text-subtle-default);
    padding: var(--spacing-2) var(--spacing-4);
    background-color: var(--colors-common-background-default);

    &__select {
        width: 60px;
    }

    &__navigation {
        display: flex;
        gap: var(--spacing-3);
    }

    &__limit > :first-child {
        margin-right: var(--spacing-1);
    }
}
</style>

<style lang="scss">
.table-pagination__select-menu {
    max-width: 120px;
}
</style>
